import { Box, FormControl, Select, MenuItem, Switch, Typography, Alert, Button, Tabs, Tab, AlertTitle, Link } from '@mui/material';
import { ApiQueries, DaymapIcon, DaymapIconType, useApp, useAppState, useSettings, DaymapSkeleton, useSchool, AssessmentTaskList, AssessmentSummary, AssessmentSchedule, StudentQCESubjects, useQCETab, AssessmentDashboard } from 'daymap-react';
import { useLocation, useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { PageContainer, PageDescriptor } from '../../../components/layout';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { SchoolClass } from 'daymap-react/dist/helpers/daymaptypes';

type AssessmentState = {
    selectedClassId: number;
    selectedDateRange: ApiQueries.assessment.AssessmentTaskListDateRange;
    onlyPublishedResults: boolean;
    tab: number;
}

function Assessment() {
    const {settings} = useSettings();
    const [state, setState] = useAppState<AssessmentState>("StudentAssessments", { 
        selectedClassId: 0,
        selectedDateRange: ApiQueries.assessment.AssessmentTaskListDateRange.Current,
        onlyPublishedResults: false,
        tab: settings?.showAssessmentSummary ? 0 : 1    
    }, "session");
    const location = useLocation();
    const navigate = useNavigate();
    const { restoreScrollPosition } = useApp();
    const { studentId } = useParams();
    const { isPending, error, data } = new ApiQueries.student.DataForStudent({studentId: Number(studentId),}).ToQuery();
    const showQCETab = useQCETab(Number(studentId));

    useEffect(() => {
        restoreScrollPosition(location.key);
    }, []);

    if (error) {
            console.error('Error getting data', error);
            return <Alert color="error">
                {error.title && <AlertTitle>{error.title}</AlertTitle>}
                {error.detail}
            </Alert>
    }

    if (isPending)
        return <PageContainer>
            <DaymapSkeleton type="page" />
        </PageContainer>

    const { selectedClassId, selectedDateRange, onlyPublishedResults, tab } = state;

    // If change from a showQCETab student to a not showQCETab student, then reset tab to 0.
    if (tab === 3 && !showQCETab)
        setState(prev => ({...prev, tab: 0}));

    const selectedClass = data?.classes?.find((c: SchoolClass) => c.Id === selectedClassId);
    const selectedClassCode = selectedClassId === 0 ? "" : (selectedClass?.ClassCode || "");

    const handleTaskClick = (taskId: number) => {
        navigate(`/student/${studentId}/assessment/${taskId}`);
    };

    return (
        <PageContainer>
            <PageDescriptor icon={<DaymapIcon iconType={DaymapIconType.Assessment} />} title="Assessment">
                View current assessment details and published results
            </PageDescriptor>
            <AssessmentDashboardLink />
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(_, x:number) => setState({ ...state, tab: x })} aria-label="basic tabs example" variant="scrollable" scrollButtons allowScrollButtonsMobile>
                    {settings?.showAssessmentSummary ? <Tab label="Summary" id={`assessment-tab-${0}`}/> : <Tab sx={{ display: 'none' }} disabled /> /*preserve indexes*/}
                    <Tab label="Assessment Tasks"  id={`assessment-tab-${1}`}/>
                    <Tab label="Schedule" id={`assessment-tab-${2}`}/>
                    {showQCETab && <Tab label="QCE Results" id={`assessment-tab-${3}`}/>}
                </Tabs>
            </Box>
            {tab !== 2 && tab !== 3 && <AssessmentForm
                    studentId={Number(studentId)}
                    selectedClassId={selectedClassId}
                    onClassSelected={v => setState({ ...state, selectedClassId: v })}
                    selectedDateRange={selectedDateRange}
                    onDateRangeSelected={v => setState({ ...state, selectedDateRange: v })}
                    onlyPublishedResults={onlyPublishedResults}
                    onTogglePublishedResults={v => setState({ ...state, onlyPublishedResults: v })}
                    showPublishedCheckbox={tab != 0}
                />}
            {tab == 0 && <AssessmentSummary studentId={Number(studentId)} classCode={selectedClassCode} selectedDateRange={selectedDateRange} onTaskClick={handleTaskClick}/>}
            {tab == 1 && <AssessmentTaskList studentId={Number(studentId)} classCode={selectedClassCode} selectedDateRange={selectedDateRange} onlyPublishedResults={onlyPublishedResults} onTaskClick={handleTaskClick}/>}
            {tab == 2 && <AssessmentSchedule studentId={Number(studentId)} onTaskClick={handleTaskClick}/>}
            {tab == 3 && showQCETab && <StudentQCESubjects studentId={Number(studentId)} year={(new Date()).getFullYear()} />}
        </PageContainer>
    );
}

function AssessmentDashboardLink() {
    const { settings, isAtLeastThisVersion }  = useSettings();
    const { baseUrl } = useSchool();
    const { studentId } = useParams();
    
    if (!isAtLeastThisVersion(11, 7)) {
        const dashUrl = `${baseUrl}/daymapconnect/portfolio.aspx?id=${studentId}&pageid=5`;
        const openAssessmentDashboard = () => {
            Browser.open({ url: dashUrl });
        }

        return <Alert severity='info'>
            Assessment dashboards and progress reports are not yet available in Daymap Companion.&nbsp;
            {Capacitor.isNativePlatform() ?
                <Button startIcon={<DaymapIcon iconType={DaymapIconType.NewWindow} />} variant="text" color="info" onClick={openAssessmentDashboard}>
                    Click here to view in Daymap Connect
                </Button>
                :
                <Link component={RouterLink}
                    to={dashUrl}
                    target="_blank"
                    color="info">
                    Click here to view in Daymap Connect.
                </Link>
            }
        </Alert>
    } 

    if (!settings?.portalShowAssessDash)
        return null;

    return <AssessmentDashboard studentId={Number(studentId)} />
}

type AssessmentFormProps = {
    studentId: number;
    selectedClassId: number;
    onClassSelected: (classId: number) => void;
    selectedDateRange: ApiQueries.assessment.AssessmentTaskListDateRange;
    onDateRangeSelected: (dateRange: ApiQueries.assessment.AssessmentTaskListDateRange) => void;
    onlyPublishedResults: boolean;
    onTogglePublishedResults: (value: boolean) => void;
    showPublishedCheckbox: boolean;
};

const AssessmentForm: React.FC<AssessmentFormProps> = ({
    studentId,
    selectedClassId,
    onClassSelected,
    selectedDateRange,
    onDateRangeSelected,
    onlyPublishedResults,
    onTogglePublishedResults,
    showPublishedCheckbox
}) => {
    const { isPending, error, data } = new ApiQueries.student.DataForStudent({studentId}).ToQuery();

    if (isPending)
        return <DaymapSkeleton type="form" />;

    if (error) {
        console.error('Error getting data', error);
        return <Alert color="error">
            {error.title && <AlertTitle>{error.title}</AlertTitle>}
            {error.detail}
        </Alert>
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <FormControl>
                <Select
                    labelId="classList-label"
                    id="classList"
                    value={selectedClassId}
                    onChange={(e) => onClassSelected(Number(e.target.value))}
                >
                    <MenuItem value={0}>All Classes</MenuItem>
                    {data?.classes?.map((classItem: SchoolClass) => (<MenuItem key={classItem.Id} value={classItem.Id}>{classItem.ClassName}</MenuItem>))}
                </Select>
                </FormControl>
                <FormControl>
                <Select
                    labelId="year-label"
                    id="year"
                    value={selectedDateRange}
                    onChange={(e) => onDateRangeSelected(e.target.value as ApiQueries.assessment.AssessmentTaskListDateRange)}
                >
                    <MenuItem value={ApiQueries.assessment.AssessmentTaskListDateRange.Current}>Current</MenuItem>
                    <MenuItem value={ApiQueries.assessment.AssessmentTaskListDateRange.ThisYear}>All for this year</MenuItem>
                    <MenuItem value={ApiQueries.assessment.AssessmentTaskListDateRange.All}>All</MenuItem>
                </Select>
                </FormControl>
            </Box>
            {showPublishedCheckbox && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2 }}>
                <Switch
                    size="small"
                    checked={onlyPublishedResults}
                    onChange={(e) => onTogglePublishedResults(e.target.checked)}
                />
                <Typography>Only show tasks with published results</Typography>
            </Box>}
       
    </Box>
    );
};

export default Assessment;